"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon24MenuOutline, Icon24Cancel } from "@linqx/icons/web";
import { Language } from "@linqx/i18n";
import { LogoWithText } from "@/components/logo";
import { Magnet } from "@/components/reactbits/magnet";
import { cn } from "@/lib/utils";
import { scrollToId } from "@/lib/scroll";
import { Telemetry, TelemetryEvent } from "@linqx/telemetry";

const navLinks = [
  { key: "landing_nav_features", href: "#features" },
  { key: "landing_nav_about", href: "#about" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const ticking = useRef(false);

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 50);
        ticking.current = false;
      });
      ticking.current = true;
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          isScrolled
            ? "bg-background/80 backdrop-blur-md border-b border-border"
            : "bg-transparent",
        )}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 group">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <LogoWithText size={28} />
              </motion.div>
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map(({ key, href }) => (
                <motion.a
                  key={key}
                  href={href}
                  whileHover={{ y: -2 }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {Language.getKey(key)}
                </motion.a>
              ))}
              <Magnet padding={40} magnetStrength={4}>
                <button
                  className="btn-brand h-10 px-6 text-sm font-semibold rounded-full shadow-sm"
                  onClick={() => {
                    Telemetry.track(TelemetryEvent.CTA_CLICKED, {
                      location: "header",
                      cta: "join_waitlist",
                    });
                    scrollToId("waitlist");
                  }}
                >
                  {Language.getKey("landing_nav_join_waitlist")}
                </button>
              </Magnet>
            </nav>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={Language.getKey("landing_nav_toggle_menu")}
            >
              {isMobileMenuOpen ? <Icon24Cancel /> : <Icon24MenuOutline />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-16 z-40 md:hidden bg-background/95 backdrop-blur-md border-b border-border"
          >
            <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
              {navLinks.map(({ key, href }) => (
                <a
                  key={key}
                  href={href}
                  className="text-lg text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {Language.getKey(key)}
                </a>
              ))}
              <button
                className="btn-brand w-full h-12 text-base font-semibold rounded-full shadow-md"
                onClick={() => {
                  Telemetry.track(TelemetryEvent.CTA_CLICKED, {
                    location: "header_mobile",
                    cta: "join_waitlist",
                  });
                  setIsMobileMenuOpen(false);
                  scrollToId("waitlist");
                }}
              >
                {Language.getKey("landing_nav_join_waitlist")}
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
